import { useMemo, useState } from "react";
import { Redirect, useLocation } from "wouter";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Sprout, Camera, CheckCircle2, Leaf, BookOpen } from "lucide-react";
import { GardenPlanView } from "@/components/GardenPlanView";
import { GardenCameraCapture, type CapturedPhoto } from "@/components/GardenCameraCapture";
import { parseErrorMessage } from "@/lib/gardenFetch";
import type { GardenSceneGraph } from "@shared/schema";

interface PlannedPlantJson {
  slug: string;
  worldX: number;
  worldY: number;
  spacingFt: number;
  bedLabel: string | null;
}

interface GardenState {
  garden: {
    id: string;
    name: string | null;
    referencePhotoUrl: string | null;
    estimatedAreaSqFt: string | null;
    hardinessZone: string | null;
    cardinalSouthDegrees: number | null;
    sceneGraph: GardenSceneGraph | null;
    status: string;
    planJson: {
      plants?: PlannedPlantJson[];
      unplaced?: Array<{ slug: string; quantity: number; reason: string }>;
    } | null;
  } | null;
  plants: Array<{
    id: string;
    plantSlug: string;
    quantity: number;
    status: string;
    startMethod: string;
    plantedAt: string | null;
    seedStartDate: string | null;
    transplantDate: string | null;
    estimatedHarvestDate: string | null;
    gridX: number | null;
    gridY: number | null;
    notesJson: any;
  }>;
  photos: Array<{ id: string; photoUrl: string; kind: string; takenAt: string | null }>;
  issues: Array<{ id: string; diagnosisJson: any; createdAt: string | null }>;
  reservedZones: Array<{
    id: string;
    label: string;
    kind: string;
    plantSlug: string | null;
    normX: string;
    normY: string;
    normWidth: string;
    normHeight: string;
  }>;
  reminders: Array<{
    id: string;
    kind: string;
    dueAt: string;
    firedAt: string | null;
    gardenPlantId: string | null;
    payloadJson: { title?: string; body?: string } | null;
  }>;
}

interface CatalogEntry {
  slug: string;
  commonName: string;
  family: string;
}

function fmt(date: string | null | undefined): string {
  if (!date) return "—";
  try {
    return new Date(date).toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch {
    return "—";
  }
}

export default function Garden() {
  const [, setLocation] = useLocation();
  const queryClient = useQueryClient();
  const [selectedPlantId, setSelectedPlantId] = useState<string | null>(null);

  const { data: state, isLoading } = useQuery<GardenState>({
    queryKey: ["/api/garden"],
    queryFn: async () => {
      const res = await fetch("/api/garden", { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load garden");
      return res.json();
    },
  });

  const { data: catalogData } = useQuery<{ catalog: CatalogEntry[] }>({
    queryKey: ["/api/garden/catalog"],
    queryFn: async () => {
      const res = await fetch("/api/garden/catalog", { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load catalog");
      return res.json();
    },
  });

  const confirmPlanted = useMutation({
    mutationFn: async (plantId: string) => {
      const res = await fetch(`/api/garden/plants/${plantId}/confirm-planted`, {
        method: "POST",
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/garden"] });
    },
  });

  const uploadWeeklyPhoto = useMutation({
    mutationFn: async (input: { photo: CapturedPhoto; plantId: string | null }) => {
      const res = await fetch("/api/garden/photos", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          photoDataUrl: input.photo.dataUrl,
          gardenPlantId: input.plantId ?? undefined,
        }),
      });
      if (!res.ok) throw new Error(await parseErrorMessage(res, "Photo upload failed"));
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/garden"] });
    },
  });

  if (isLoading) {
    return <div className="p-6 text-muted-foreground">Loading garden…</div>;
  }

  if (!state?.garden) {
    return <Redirect to="/garden/setup" />;
  }

  if (state.garden.status === "setup" || !state.garden.estimatedAreaSqFt) {
    return <Redirect to="/garden/setup" />;
  }

  const garden = state.garden;
  const planPlants: Array<{
    id: string;
    plantSlug: string;
    worldX: number;
    worldY: number;
    spacingFt: number;
    bedLabel: string | null;
  }> = state.plants
    .map((p) => {
      const notes = p.notesJson as { worldX?: number; worldY?: number; spacingFt?: number; bedLabel?: string | null } | null;
      if (
        notes &&
        typeof notes.worldX === "number" &&
        typeof notes.worldY === "number" &&
        typeof notes.spacingFt === "number"
      ) {
        return {
          id: p.id,
          plantSlug: p.plantSlug,
          worldX: notes.worldX,
          worldY: notes.worldY,
          spacingFt: notes.spacingFt,
          bedLabel: notes.bedLabel ?? null,
        };
      }
      return null;
    })
    .filter((p): p is NonNullable<typeof p> => p !== null);

  const plantDateLabel = (p: GardenState["plants"][number]): string => {
    const parts: string[] = [];
    if (p.seedStartDate) parts.push(`Seed start ${fmt(p.seedStartDate)}`);
    if (p.transplantDate) {
      const label = p.startMethod === "direct_sow" ? "Direct sow" : "Plant";
      parts.push(`${label} ${fmt(p.transplantDate)}`);
    }
    if (p.estimatedHarvestDate) parts.push(`Harvest ~${fmt(p.estimatedHarvestDate)}`);
    return parts.join(" · ");
  };

  const upcomingReminders = [...state.reminders]
    .filter((r) => !r.firedAt)
    .sort((a, b) => new Date(a.dueAt).getTime() - new Date(b.dueAt).getTime())
    .slice(0, 6);

  return (
    <div className="mx-auto max-w-4xl space-y-4 p-4 pb-24">
      <div className="flex items-center justify-between gap-2">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold">
            <Sprout className="h-6 w-6" /> Garden
          </h1>
          <p className="text-sm text-muted-foreground">
            {garden.estimatedAreaSqFt} sq ft · Zone {garden.hardinessZone ?? "—"}
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => setLocation("/garden/setup")}>
          Edit plan
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Overview</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-3 gap-4 text-center">
          <div>
            <div className="text-2xl font-semibold">{state.plants.length}</div>
            <div className="text-xs text-muted-foreground">Plants</div>
          </div>
          <div>
            <div className="text-2xl font-semibold">
              {state.plants.filter((p) => p.plantedAt).length}
            </div>
            <div className="text-xs text-muted-foreground">In the ground</div>
          </div>
          <div>
            <div className="text-2xl font-semibold">{upcomingReminders.length}</div>
            <div className="text-xs text-muted-foreground">Upcoming tasks</div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Upcoming schedule</CardTitle>
        </CardHeader>
        <CardContent>
          {upcomingReminders.length === 0 ? (
            <p className="text-sm text-muted-foreground">All caught up.</p>
          ) : (
            <ul className="space-y-2">
              {upcomingReminders.map((r) => (
                <li key={r.id} className="flex items-start justify-between gap-2 text-sm">
                  <div>
                    <div className="font-medium">{r.payloadJson?.title ?? r.kind}</div>
                    <div className="text-xs text-muted-foreground">
                      {r.payloadJson?.body ?? ""}
                    </div>
                  </div>
                  <Badge variant="secondary">{fmt(r.dueAt)}</Badge>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Layout</CardTitle>
        </CardHeader>
        <CardContent>
          {garden.sceneGraph ? (
            <GardenPlanView
              scene={garden.sceneGraph}
              plants={planPlants}
              catalog={catalogData?.catalog ?? []}
              cardinalSouthDegrees={garden.cardinalSouthDegrees}
            />
          ) : (
            <p className="text-sm text-muted-foreground">
              Sketch your garden layout in setup to see the plan rendered here.
            </p>
          )}
          {garden.planJson?.unplaced && garden.planJson.unplaced.length > 0 ? (
            <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-200">
              <div className="font-medium">Some plants didn't fit in your beds:</div>
              <ul className="mt-1 ml-4 list-disc space-y-0.5">
                {garden.planJson.unplaced.map((u, i) => (
                  <li key={i}>
                    {u.slug} ×{u.quantity} — {u.reason}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Planting checklist</CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="space-y-2">
            {state.plants.map((p) => {
              const catalogEntry = catalogData?.catalog.find((c) => c.slug === p.plantSlug);
              return (
                <li
                  key={p.id}
                  className="flex items-center justify-between gap-2 border-b pb-2 last:border-none"
                >
                  <div className="text-sm">
                    <div className="font-medium">
                      {catalogEntry?.commonName ?? p.plantSlug}
                      {p.quantity > 1 ? ` · ${p.quantity}` : ""}
                    </div>
                    <div className="text-xs text-muted-foreground">{plantDateLabel(p)}</div>
                  </div>
                  {p.plantedAt ? (
                    <Badge variant="default">
                      <CheckCircle2 className="mr-1 h-3 w-3" /> Planted
                    </Badge>
                  ) : (
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={confirmPlanted.isPending}
                      onClick={() => confirmPlanted.mutate(p.id)}
                    >
                      Mark planted
                    </Button>
                  )}
                </li>
              );
            })}
          </ul>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Weekly photo check-in</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <select
            className="w-full rounded border bg-background p-2 text-sm"
            value={selectedPlantId ?? ""}
            onChange={(e) => setSelectedPlantId(e.target.value || null)}
          >
            <option value="">Whole garden (no specific plant)</option>
            {state.plants.map((p) => {
              const c = catalogData?.catalog.find((x) => x.slug === p.plantSlug);
              return (
                <option key={p.id} value={p.id}>
                  {c?.commonName ?? p.plantSlug}
                </option>
              );
            })}
          </select>
          <GardenCameraCapture
            label="Snap a photo"
            onCaptured={(photo) =>
              uploadWeeklyPhoto.mutate({ photo, plantId: selectedPlantId })
            }
          />
          {uploadWeeklyPhoto.data?.analysis ? (
            <div className="rounded border p-3 text-sm">
              <div className="font-medium">
                Growth stage: {uploadWeeklyPhoto.data.analysis.growthStage}
              </div>
              {uploadWeeklyPhoto.data.analysis.estimatedDaysToHarvest != null ? (
                <div className="text-xs text-muted-foreground">
                  ~{uploadWeeklyPhoto.data.analysis.estimatedDaysToHarvest} days to harvest
                </div>
              ) : null}
              {uploadWeeklyPhoto.data.analysis.healthFlags?.length ? (
                <ul className="mt-1 list-disc pl-4 text-xs text-destructive">
                  {uploadWeeklyPhoto.data.analysis.healthFlags.map((f: string, i: number) => (
                    <li key={i}>{f}</li>
                  ))}
                </ul>
              ) : null}
              {uploadWeeklyPhoto.data.analysis.notes ? (
                <div className="mt-1 text-xs">{uploadWeeklyPhoto.data.analysis.notes}</div>
              ) : null}
            </div>
          ) : null}
          {state.photos.length > 0 ? (
            <div className="grid grid-cols-4 gap-2">
              {state.photos.slice(0, 8).map((ph) => (
                <a key={ph.id} href={ph.photoUrl} target="_blank" rel="noreferrer">
                  <img
                    src={ph.photoUrl}
                    alt="Garden photo"
                    className="aspect-square w-full rounded object-cover"
                  />
                </a>
              ))}
            </div>
          ) : null}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Leaf className="h-4 w-4" /> Photograph a plant
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <Button variant="outline" onClick={() => setLocation("/garden/diagnose")}>
            <Camera className="mr-2 h-4 w-4" />
            Identify or diagnose
          </Button>
          <p className="text-xs text-muted-foreground">
            Take a photo to identify what a plant is, or to diagnose an issue.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <BookOpen className="h-4 w-4" /> Plant journal
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <Button variant="outline" onClick={() => setLocation("/garden/journal")}>
            <BookOpen className="mr-2 h-4 w-4" />
            Open journal
          </Button>
          <p className="text-xs text-muted-foreground">
            Past identifications, follow-up Q&amp;A, and progress check-ins.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
